/**
 * Excel workbook sheet selection helpers.
 *
 * The inventory import UI historically read workbook.SheetNames[0]. That breaks
 * when a user saves a modified workbook and Excel reorders the sheets, or when
 * the inventory data lives on a sheet whose name is not first. These helpers
 * pick the sheet(s) that actually contain inventory data.
 */

const INVENTORY_SHEET_KEYWORDS = [
  'inventory',
  'import',
  'items',
  'products',
  'catalog',
  'list',
  'count',
  'setup',
  'onboarding',
  'stock',
];

/**
 * Score a sheet name by how strongly it suggests an inventory/import sheet.
 * Returns a non-negative integer; higher is a better match.
 */
function scoreSheetName(name) {
  const normalized = String(name).toLowerCase().replace(/[_\-]+/g, ' ');
  let score = 0;
  for (const keyword of INVENTORY_SHEET_KEYWORDS) {
    if (normalized.includes(keyword)) score += 1;
  }
  return score;
}

/**
 * Pick the single best sheet to use for an inventory import.
 *
 * Rules:
 *   1. If there is only one sheet, use it.
 *   2. Prefer sheets whose names contain inventory/import keywords.
 *   3. Fall back to the first non-empty sheet.
 *   4. Final fallback: the first sheet (same as the old behavior).
 *
 * @param {object} workbook - SheetJS workbook object ({ SheetNames, Sheets }).
 * @returns {string|null} The selected sheet name, or null if no sheet exists.
 */
export function selectInventorySheetName(workbook) {
  if (!workbook || !Array.isArray(workbook.SheetNames) || workbook.SheetNames.length === 0) {
    return null;
  }

  const names = workbook.SheetNames;
  if (names.length === 1) return names[0];

  const scored = names.map((name) => ({ name, score: scoreSheetName(name) }));
  scored.sort((a, b) => b.score - a.score);

  if (scored[0].score > 0) {
    return scored[0].name;
  }

  // No inventory-named sheet found — use the first sheet that has content.
  for (const name of names) {
    const sheet = workbook.Sheets[name];
    if (!sheet) continue;
    const csv = (typeof XLSX !== 'undefined')
      ? XLSX.utils.sheet_to_csv(sheet, { blankrows: false })
      : '';
    if (String(csv).trim().length > 0) {
      return name;
    }
  }

  return names[0];
}

/**
 * Return an array of sheet names that should be considered for an inventory
 * import. This is the multi-sheet variant of selectInventorySheetName: it
 * returns every sheet that looks inventory-related, and falls back to the first
 * non-empty sheet if none match.
 */
export function selectInventorySheetNames(workbook) {
  const best = selectInventorySheetName(workbook);
  if (!best) return [];

  const names = workbook.SheetNames;
  const inventoryNames = names.filter((name) => scoreSheetName(name) > 0);
  if (inventoryNames.length > 0) {
    // Preserve original workbook order.
    return names.filter((name) => inventoryNames.includes(name));
  }
  return [best];
}
